import { useEffect, useRef, useState } from 'react';
import { multiBoxListOrNone } from '@/utils/orderBoxes';
import CostBreakdown from '@/components/CostBreakdown/CostBreakdown';
import { costAmounts } from '@/components/CostBreakdown/costAmounts';
import type { LargeOrderFeeReason } from '@/types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  ArrowLeft,
  Package,
  User,
  MapPin,
  Truck,
  CreditCard,
  FileText,
  Snowflake,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { ROUTE } from '@/config';
import { cn } from '@/lib/utils';
import OrderStepper from '@/components/OrderStepper/OrderStepper';
import useNewOrderStore from '@/stores/useNewOrderStore';
import useUserStore, { selectUsesServices } from '@/stores/userStore';
import useProductStore from '@/stores/productStore';
import { useCreateOrder, useGetAvailableCarriers, useUpdateSlotPreference } from '@/hooksApi';
import { isSlotSelectionRequired } from '@/components/PickupSlots/slotGate';
import useSlotSelectionStore from '@/stores/useSlotSelectionStore';
import type { PreferredTiming } from '@/types';
import { serviceLaneRefusal } from '@/utils/carrierRefusalReason';
import { usePreviewOrderPrice, useUpdateOrder } from '@/hooksApi/useOrder';
import { buildAvailableCarriersRequest, formatPhoneDisplay } from '@/utils';
import { AvailableCarrierItem, AvailableCarriersAdaptedResponse, ServiceTypeEnum } from '@/types';
import { useFetchPickupAddresses } from '@/hooksApi/usePickupAddress';
import useServiceStepDecision from '@/hooks/useServiceStepDecision';
import useHasServiceStep from '@/hooks/useHasServiceStep';
import useHasServiceChoice from '@/hooks/useHasServiceChoice';
import ServiceTraceLine from '@/components/OrderForm/ServiceTraceLine';
import UnavailablePreferenceNotice from '@/components/OrderForm/UnavailablePreferenceNotice';
import { useFetchServicePreferences } from '@/hooksApi';
import fragileIcon from '@/assets/fragile.svg';

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <div className='flex items-start justify-between gap-4 py-2'>
    <span className='text-sm text-sd-muted-foreground shrink-0 max-w-[45%]'>{label}</span>
    <span className='text-sm font-medium text-end break-words max-w-[55%]'>{value}</span>
  </div>
);

const SectionHeader = ({ icon: Icon, title }: { icon: LucideIcon; title: string }) => (
  <div className='flex items-center gap-2 mb-4'>
    <Icon className='h-4 w-4 text-sd-muted-foreground' strokeWidth={1.5} />
    <h2 className='text-sm font-bold uppercase tracking-wider text-sd-foreground'>{title}</h2>
  </div>
);

// Label mappers are defined inside the component to access t() for i18n

const OrderSummary = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // No carrier anywhere on this page (owner 2026-10-28) — for every merchant.

  // SVC-STEP increment 2 — the audit trail for a step that may have skipped
  // itself. Same decision source as both navigations, so the summary can never
  // describe a skip the flow did not make.
  // QA INC2-F2 — read from contactDisplayInfo, NOT savedProvinceCode: only the
  // create flow ever wrote the latter, so an edited order resolved this trace
  // destination-blind and could name the wrong rung entirely.
  const traceProvinceCode = useNewOrderStore((state) => state.contactDisplayInfo?.provinceCode);
  const { resolution: serviceResolution } = useServiceStepDecision(traceProvinceCode);
  // The SAME predicate the rail draws itself from, so the breadcrumb cannot
  // name a step the wizard does not have — or omit one it does.
  const hasServiceStep = useHasServiceStep(traceProvinceCode);
  // NOT `hasServiceStep`: a merchant whose rules already decided has no step in
  // the flow, and [Change] is exactly how they reach it anyway. The only thing
  // that makes the link dead is having nothing to change TO — the same count
  // SelectService bounces on.
  // BOXY-530 — SelectService also narrows to the resolve answer's
  // `available_service_codes`, so a destination one service covers (Basra)
  // bounces too. Absent (older backend) = no narrowing, link stays.
  const coveredCodes = serviceResolution?.available_service_codes;
  const canChangeService =
    useHasServiceChoice() && !(Array.isArray(coveredCodes) && coveredCodes.length < 2);
  // QA D5 — did the merchant actually WALK the service step to get here?
  // Navigation state, not the store: it answers "how did you arrive", which is
  // exactly the question, and it dies on a refresh — which is correct, because
  // a refresh loses `orderPayload` and bounces the merchant out of the wizard
  // anyway. Presence outranks prediction on EVERY surface, not just the step.
  const cameFromServiceStep = Boolean(
    (useLocation().state as { fromServiceStep?: boolean } | null)?.fromServiceStep,
  );
  const { data: serviceCatalogue } = useFetchServicePreferences();
  // No plan (setting.uses_services=false) = no service named here, none sent.
  const usesServices = useUserStore(selectUsesServices);

  const getPaymentLabel = (type: string | undefined) => {
    if (!type) return '—';
    const map: Record<string, string> = {
      COLLECT_ON_DELIVERY: String(t('cod')),
      PREPAID: String(t('prepaid')),
    };
    return map[type] || type;
  };

  const getSizeLabel = (size: string | undefined) => {
    if (!size) return '—';
    const map: Record<string, string> = {
      XS: String(t('extra_small', 'Extra Small')),
      S: String(t('small')),
      M: String(t('medium')),
      L: String(t('large')),
      XL: String(t('extra_large', 'Extra Large')),
    };
    return map[size] || size;
  };

  const getOrderTypeLabel = (type: string | undefined) => {
    if (!type) return '—';
    const map: Record<string, string> = {
      DEFAULT: String(t('normal')),
      RETURN: String(t('return_order')),
      EXCHANGE: String(t('exchange')),
    };
    return map[type] || type;
  };

  const getPickupLabel = (type: string | undefined) => {
    if (!type) return '—';
    const map: Record<string, string> = {
      PICK_UP: String(t('boxy_collects')),
      DROP_OFF: String(t('drop_off', 'Drop-off')),
    };
    return map[type] || type;
  };
  const {
    orderPayload,
    selectedCarrier,
    setSelectedCarrier,
    setCarrierSelection,
    sourceRegionUid,
    contactDisplayInfo,
    isEditMode,
    editOrderUid,
    reset,
  } = useNewOrderStore();

  const { resetProducts } = useProductStore();
  // The service actually being shipped on: the merchant's explicit pick if
  // they made one, otherwise whatever the ladder resolved. Title comes from
  // the catalogue so it is localized, never the raw code.
  const serviceTraceCode = orderPayload?.service_code ?? serviceResolution?.service_code ?? null;
  const serviceTraceTitle =
    serviceCatalogue?.services?.find((service) => service.code === serviceTraceCode)?.title ?? '';

  const { mutate: createOrder } = useCreateOrder();
  const { mutate: updateOrder } = useUpdateOrder(editOrderUid ?? '');
  const { mutateAsync: fetchCarriers } = useGetAvailableCarriers();
  const { data: pickupAddressesData } = useFetchPickupAddresses();
  const { openSlotSelection } = useSlotSelectionStore();
  const { mutateAsync: updateSlotPreference } = useUpdateSlotPreference();

  const { mutateAsync: previewOrderPrice } = usePreviewOrderPrice();

  const [submitting, setSubmitting] = useState<'draft' | 'ship' | undefined>(undefined);
  // R54-F1 — the fee the CHARGE will use, from /orders/preview.
  const [previewFee, setPreviewFee] = useState<number | undefined>(undefined);
  /**
   * BOXY-301 R4 — the per-box breakdown, straight from the same /orders/preview
   * response the Shipping line is read from.
   *
   * Taken from the preview rather than the form's own box list on purpose: the
   * form knows the SIZES, only the backend knows what each one is CHARGED, and
   * a client that recomputed the 50% rule would be a second implementation of
   * the pricing that quotes and bills. The two would drift the first time one
   * of them changed.
   */
  const [previewBoxes, setPreviewBoxes] = useState<
    { sequence: number; size: string; fee: number; charged: number }[] | undefined
  >(undefined);
  // BOXY-311 §9.4 — the large-order fee line, from the SAME preview call. For a
  // Boxy Select merchant this is the single blended quote's breakdown (§9.2).
  const [previewLargeFee, setPreviewLargeFee] = useState<
    { fee: number; reason: LargeOrderFeeReason } | undefined
  >(undefined);
  // FS-2a — the reason /orders/preview refused this order, if it did.
  const [previewRefusal, setPreviewRefusal] = useState<string | undefined>(undefined);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const retryCountRef = useRef(0);
  const slotRetryRef = useRef(0);

  useEffect(() => {
    // Only redirect to step 1 if data is missing AND we're not in the middle of submitting
    // (reset() clears the store on success, which would trigger this before navigate fires)
    if (!orderPayload || !selectedCarrier) {
      if (!submitting) {
        navigate(isEditMode && editOrderUid ? `/orders/edit/${editOrderUid}` : ROUTE.ADDNEWORDER, {
          replace: true,
        });
      }
    }
  }, [orderPayload, selectedCarrier, navigate, isEditMode, editOrderUid, submitting]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
      setIsScrolledUp(!isAtBottom);
    };
    handleScroll();
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  // R54-F1 — the Cost Summary is priced by /orders/preview, not by the carrier
  // session. `selectedCarrier.shipping_fee` comes from POST
  // /orders/available-carriers, which quotes a carrier BEFORE the order exists;
  // /orders/preview is the same computation `/orders/request` will charge on
  // (MJ-F1), through the same service resolver. Round 54 found the two
  // disagreeing on a real account — summary 7,750 against a 5,000 charge —
  // because available-carriers priced on no service at all while creation
  // priced on the merchant's standing preference. The backend fix closes that
  // gap, but the two numbers are still produced by two different calls at two
  // different times: this makes the one a person reads the one they will pay.
  //
  // Falls back to the session fee while the call is in flight and if it fails,
  // so the summary always shows a number. `process_id`/`selected_carrier_slug`
  // are never sent: no merchant picks a carrier — see R26-F1 in submitOrder.
  useEffect(() => {
    if (!orderPayload || !selectedCarrier) return;

    let stale = false;

    previewOrderPrice({
      ...orderPayload,
      // A one-element list only means "collapse to one box" on an edit; the
      // preview refuses it, so it prices the one box by `size` instead.
      boxes: multiBoxListOrNone(orderPayload.boxes),
    } as any)
      .then((response) => {
        if (stale) return;
        const fee = response?.object?.orders_shipment_fee;
        if (typeof fee === 'number') setPreviewFee(fee);

        // R4 — present ONLY on a multi-box quote; the backend omits all three
        // keys for a single parcel, so this stays undefined and the Summary
        // renders exactly as it does today.
        const boxes = response?.object?.boxes;
        setPreviewBoxes(Array.isArray(boxes) && boxes.length > 1 ? boxes : undefined);

        const largeFee = response?.object?.large_order_fee_iqd;
        const largeReason = response?.object?.large_order_fee_reason;
        setPreviewLargeFee(
          typeof largeFee === 'number' && largeFee > 0 && largeReason
            ? { fee: largeFee, reason: largeReason }
            : undefined,
        );

        setPreviewRefusal(undefined);
      })
      .catch((error: any) => {
        if (stale) return;

        // FS-2a — /orders/preview now refuses exactly what /orders/request
        // refuses (no carrier can serve this order; the picked carrier was
        // never offered). Swallowing that left the merchant reading a price
        // for an order the very next click could not create — the same
        // quote-versus-charge gap R54-F1 closed, one step earlier. Show the
        // backend's own localized sentence and stop the submit.
        //
        // Anything else (network, 500) keeps the old behaviour: fall back to
        // the session fee rather than block a create that would have worked.
        const refusal =
          error?.response?.status === 422 ? error?.response?.data?.message : undefined;
        setPreviewRefusal(typeof refusal === 'string' && refusal ? refusal : undefined);
      });

    return () => {
      stale = true;
    };
  }, [orderPayload, selectedCarrier, previewOrderPrice]);

  if (!orderPayload || !selectedCarrier) return null;

  // Every fee the merchant reads below comes from here, so the Shipping line,
  // the Total and "Customer Will Pay" can never disagree with each other.
  const shippingFee = previewFee ?? selectedCarrier.shipping_fee;

  const contact = orderPayload.contact;
  const products = orderPayload.products ?? [];
  const totalProducts = products.reduce(
    (sum: number, p: any) => sum + (parseFloat(p.price) || 0) * (p.quantity || 1),
    0,
  );
  // The same split the order details card uses, so the two screens agree —
  // including a custom customer fee on an edited BY_CUSTOMER order.
  const amounts = costAmounts({
    goods: totalProducts,
    shippingFee,
    feeCustomerPayable: orderPayload.fee_customer_payable,
    byCustomer: orderPayload.shipment_fee_type === 'BY_CUSTOMER',
    prepaid: orderPayload.payment_type === 'PREPAID',
    largeOrderFee: previewLargeFee?.fee ?? 0,
  });
  const customerPays = amounts.customerPays;

  // Resolve source_region_uid from store or pickup addresses
  const resolveSourceRegionUid = (): string | undefined => {
    if (sourceRegionUid) return sourceRegionUid;
    if (!orderPayload) return undefined;
    const addr = pickupAddressesData?.object?.items?.find(
      (a) =>
        a.uid === orderPayload.saved_pick_up_address_uid ||
        a.uid === orderPayload.pick_up_address_uid,
    );
    return addr?.region?.uid;
  };

  // Dead create session. There is no carrier step to be sent back to — and
  // no merchant is told about "carriers" — so they go back to the order form.
  const bailOutOfDeadSession = () => {
    setSubmitting(undefined);
    message.warning(String(t('boxy_select_session_expired')));
    navigate(isEditMode && editOrderUid ? `/orders/edit/${editOrderUid}` : ROUTE.ADDNEWORDER, {
      replace: true,
    });
  };

  // Silently refresh the carrier session and retry order submission
  const refreshSessionAndRetry = async (readyToShip: boolean) => {
    const regionUid = resolveSourceRegionUid();
    if (!orderPayload || !regionUid || !selectedCarrier) {
      bailOutOfDeadSession();
      return;
    }

    try {
      const carrierRequest = buildAvailableCarriersRequest(orderPayload, regionUid);
      const response: AvailableCarriersAdaptedResponse = await fetchCarriers(carrierRequest);

      // Rebuild available carriers list
      const allRawCarriers = [
        ...(response.object?.same_day_carriers ?? []),
        ...(response.object?.next_day_carriers ?? []),
      ];

      const available: AvailableCarrierItem[] = allRawCarriers.map((c: any) => ({
        carrier_slug: c.carrier?.slug || '',
        carrier_name: c.carrier?.title || '',
        carrier_logo_url: c.carrier?.avatar?.abs_url || '',
        shipping_fee: parseFloat(c.shipping_fee) || 0,
        delivery_day:
          c.serviceType === ServiceTypeEnum.SAME_DAY || c.average_delivery_time === 'same_day'
            ? ('same' as const)
            : ('next' as const),
        same_day_cutoff: c.serviceType === ServiceTypeEnum.SAME_DAY ? c.if_ship_before : null,
        drop_off_time: c.serviceType === ServiceTypeEnum.NEXT_DAY ? c.if_ship_before : null,
      }));

      // Verify the selected carrier is still available
      const stillAvailable = available.some(
        (c) =>
          c.carrier_slug === selectedCarrier.carrier_slug &&
          c.delivery_day === selectedCarrier.delivery_day,
      );

      if (!stillAvailable) {
        setCarrierSelection({
          process_id: response.process_id,
          recommended_carrier_slug: response.recommended_carrier_slug,
          available_carriers: available,
        });
        // The merchant never picked this carrier, so there is nothing to send
        // them back to re-pick and nothing to tell them. Take the fresh one
        // and retry — same move AddNewOrder makes (`setSelectedCarrier(available[0])`).
        if (available[0]) {
          setSelectedCarrier(available[0]);
          submitOrder(readyToShip);
        } else {
          bailOutOfDeadSession();
        }
        return;
      }

      // Update store with new session
      setCarrierSelection({
        process_id: response.process_id,
        recommended_carrier_slug: response.recommended_carrier_slug,
        available_carriers: available,
      });

      // Retry the submission
      submitOrder(readyToShip);
    } catch {
      bailOutOfDeadSession();
    }
  };

  // Core order submission logic
  const submitOrder = (readyToShip: boolean) => {
    const enrichedPayload = {
      ...orderPayload,
      // No plan = no service_code at all; the backend applies its default.
      ...(usesServices ? {} : { service_code: undefined }),
      ready_to_pick_up: readyToShip,
      // R26-F1: never send a carrier session. No merchant picks a carrier
      // (owner 2026-10-28), so `selectedCarrier` here is only the first entry
      // the availability check returned — not a choice. The backend picks the
      // carrier (Select: at `collected`; otherwise its rules + dispatch).
    };

    const mutateFn = isEditMode ? updateOrder : createOrder;
    mutateFn(enrichedPayload, {
      onSuccess: () => {
        retryCountRef.current = 0;
        message.success(
          String(t(isEditMode ? 'order_edited_successfully' : 'order_added_successfully')),
        );
        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries(['order']);
        queryClient.invalidateQueries(['pickups']);
        queryClient.invalidateQueries(['order-count']);
        // Navigate FIRST, then reset store — reset triggers the guard useEffect
        // which would redirect to step 1 if we reset before navigating
        const destination = readyToShip ? ROUTE.PICKUPS : `${ROUTE.ORDERS}?activeTab=ready_to_ship`;
        navigate(destination, { replace: true });
        reset();
        resetProducts(); // O-7: Also reset product store so next create order starts fresh
        setSubmitting(undefined);
      },
      onError: (error: any) => {
        const errorCode = error?.response?.data?.code;
        const errorMsg = error?.response?.data?.message || '';
        const isSessionExpired =
          errorCode === 300010 ||
          errorMsg.includes('session has expired') ||
          errorMsg.includes('already been used');

        if (isSessionExpired && retryCountRef.current < 1) {
          retryCountRef.current += 1;
          refreshSessionAndRetry(readyToShip);
          return;
        }

        // Mandatory slot selection. Ticking "Ship at the same time" makes this
        // a ship request too, and the backend now applies the same
        // SLOT_SELECTION_REQUIRED gate the pick-ups page already applied — a
        // merchant with no chosen slot used to get a pickup silently parked on
        // a legacy weekday slot, which the dashboard renders as no slot at all.
        //
        // Recover the way the Orders-list "Ship Now" button does (see
        // useSubmitSingleOrder): open the singleton slot modal, save the pick
        // as the merchant's preference, then re-submit. We save the preference
        // rather than passing a code through the create call because
        // /orders/request has no time_slot_code field — and saving also stops
        // the merchant hitting this same wall on their next order.
        //
        // Nothing was created by the refused request (the backend gates before
        // any write), so the re-submit is a clean first attempt, not a
        // duplicate. Guarded to one retry: a repeat gate error after we just
        // saved a slot means something else is wrong, so let it surface.
        if (isSlotSelectionRequired(error) && slotRetryRef.current < 1) {
          slotRetryRef.current += 1;
          setSubmitting(undefined);
          openSlotSelection({
            onSelect: async (timeSlotCode: string, preferredTiming?: PreferredTiming | null) => {
              try {
                await updateSlotPreference({ timeSlotCode, preferredTiming });
                setSubmitting(readyToShip ? 'ship' : 'draft');
                submitOrder(readyToShip);
              } catch {
                // updateSlotPreference already toasts via useSentryMutation.
                setSubmitting(undefined);
              }
            },
          });
          return;
        }

        // BOXY-588 — the order's service cannot reach this address (Boxy Save
        // to a remote area). Nothing was saved: send the merchant to the
        // service step with the backend's sentence. The edited order stays in
        // the store, so picking another service and continuing saves it.
        const laneRefusal = serviceLaneRefusal(error);
        if (laneRefusal) {
          retryCountRef.current = 0;
          setSubmitting(undefined);
          navigate(ROUTE.SELECT_SERVICE, { state: { serviceRefusal: laneRefusal } });
          return;
        }

        // TEMP-5: temperature-control rejections carry a bilingual, actionable
        // server message — make sure the merchant sees IT rather than field-level
        // 422 arrays. The shared mutation handler (handleErrorMessage) already
        // toasts error.message when the envelope has no field-error object, so
        // only toast here when that object path would have swallowed the message.
        const isTempControlError =
          errorCode === 'TEMP_CONTROL_NO_ELIGIBLE_CARRIER' || errorCode === 'TEMP_CONTROL_LOCKED';
        if (isTempControlError && errorMsg) {
          const fieldErrors = error?.response?.data?.object;
          const messageSwallowedByFieldArrays =
            !!fieldErrors && Object.values(fieldErrors).some((v) => Array.isArray(v));
          if (messageSwallowedByFieldArrays) {
            message.error(errorMsg);
          }
        }

        retryCountRef.current = 0;
        setSubmitting(undefined);
      },
    });
  };

  const handleSubmit = (readyToShip: boolean) => {
    retryCountRef.current = 0;
    slotRetryRef.current = 0;
    setSubmitting(readyToShip ? 'ship' : 'draft');
    submitOrder(readyToShip);
  };

  return (
    <div className='sd-scope sd-font relative flex flex-col -mx-4 sm:-mx-8 -mt-6 -mb-6 h-[calc(100dvh-65px)] overflow-hidden'>
      {/* Content */}
      <div ref={scrollRef} className='flex-1 overflow-y-auto px-4 sm:px-8 pt-4 sm:pt-6 pb-28'>
        {/* Breadcrumb */}
        <nav className='flex items-center gap-1.5 text-sm text-sd-muted-foreground mb-6'>
          <Link
            to={ROUTE.ORDERS}
            className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
          >
            {String(t('orders'))}
          </Link>
          <ChevronRight className={cn('h-3.5 w-3.5', isRTL && 'rotate-180')} strokeWidth={1.5} />
          <Link
            to={isEditMode && editOrderUid ? `/orders/edit/${editOrderUid}` : ROUTE.ADDNEWORDER}
            // Back to the edit form keeps this edit (create restores from the store).
            state={{ fromWizard: true }}
            className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
          >
            {String(t(isEditMode ? 'edit_order' : 'new_order'))}
          </Link>
          <ChevronRight className={cn('h-3.5 w-3.5', isRTL && 'rotate-180')} strokeWidth={1.5} />
          {/* The middle crumb names the service step, omitted only when the
              wizard genuinely has no middle step — the same condition the rail
              uses, so the two can never disagree. */}
          {hasServiceStep ? (
            <>
              <Link
                to={ROUTE.SELECT_SERVICE}
                className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
              >
                {String(t('select_service_step'))}
              </Link>
              <ChevronRight
                className={cn('h-3.5 w-3.5', isRTL && 'rotate-180')}
                strokeWidth={1.5}
              />
            </>
          ) : null}
          <span className='text-sd-foreground font-medium'>{String(t('summary'))}</span>
        </nav>

        <OrderStepper
          currentStep={3}
          provinceCode={traceProvinceCode}
          serviceStepPresent={cameFromServiceStep}
        />

        <div className='grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6'>
          {/* Left — Order Details */}
          <div className='flex flex-col gap-6'>
            {/* Customer */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <SectionHeader
                icon={User}
                title={String(t('customer_details', 'Customer Details'))}
              />
              <div className='flex flex-col divide-y divide-sd-border'>
                <SummaryRow label={String(t('name', 'Name'))} value={contact?.full_name ?? '—'} />
                <SummaryRow
                  label={String(t('primary_phone', 'Primary Phone'))}
                  value={formatPhoneDisplay(contact?.phone) || '—'}
                />
                {contact?.additional_phones?.[0] && (
                  <SummaryRow
                    label={String(t('secondary_phone', 'Secondary Phone'))}
                    value={formatPhoneDisplay(contact.additional_phones[0]) || '—'}
                  />
                )}
              </div>
            </section>

            {/* Delivery Address */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <SectionHeader
                icon={MapPin}
                title={String(t('delivery_address', 'Delivery Address'))}
              />
              <div className='flex flex-col divide-y divide-sd-border'>
                {contactDisplayInfo?.provinceName && (
                  <SummaryRow
                    label={String(t('province', 'Province'))}
                    value={contactDisplayInfo.provinceName}
                  />
                )}
                {contactDisplayInfo?.areaName && (
                  <SummaryRow
                    label={String(t('area', 'Area'))}
                    value={contactDisplayInfo.areaName}
                  />
                )}
                <SummaryRow
                  label={String(t('address', 'Address'))}
                  value={contact?.address_text ?? '—'}
                />
                {orderPayload.description && (
                  <SummaryRow
                    label={String(t('instructions', 'Instructions'))}
                    value={orderPayload.description}
                  />
                )}
              </div>
            </section>

            {/* Products */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <SectionHeader icon={Package} title={String(t('products', 'Products'))} />
              <div className='flex flex-col gap-2'>
                {products.map((product: any, i: number) => (
                  <div
                    key={i}
                    className='flex items-center justify-between rounded-lg bg-sd-muted/30 px-4 py-3'
                  >
                    <div>
                      <p className='text-sm font-medium'>{product.title ?? product.name}</p>
                      <p className='text-xs text-sd-muted-foreground'>
                        {String(t('qty', 'Qty'))}: {product.quantity}
                      </p>
                    </div>
                    <span className='text-sm font-semibold tabular-nums' dir='ltr'>
                      {(
                        (parseFloat(product.price) || 0) * (product.quantity || 1)
                      ).toLocaleString()}{' '}
                      {String(t('iqd'))}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {/* Service — the recap names the Boxy service, never the carrier
                behind it (owner 2026-10-28). Delivery date and type stay: they
                are service facts, not carrier identity. */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <SectionHeader icon={Truck} title={String(t('boxy_select_service_title'))} />
              <div className='flex flex-col divide-y divide-sd-border'>
                <div className='flex items-center justify-between py-2'>
                  <span className='text-sm text-sd-muted-foreground'>{String(t('service'))}</span>
                  <div className='flex items-center gap-2'>
                    <span className='text-sm font-medium'>
                      {String(t('boxy_select_service_name'))}
                    </span>
                  </div>
                </div>
                {/* SVC-STEP increment 2 — a skipped step must leave a trace.
                    The wizard may decide not to ask, but the summary then has
                    to say WHAT decided and offer one click back; a silent skip
                    is a decision the merchant cannot audit. Renders on the
                    explicit-choice path too, not only on skips. */}
                {usesServices && serviceTraceTitle ? (
                  <div className='py-2'>
                    <ServiceTraceLine
                      serviceTitle={serviceTraceTitle}
                      source={serviceResolution?.source ?? 'none'}
                      chosenOnThisOrder={Boolean(orderPayload?.service_code)}
                      provinceName={contactDisplayInfo?.provinceName}
                      canChange={canChangeService}
                    />
                  </div>
                ) : null}
                {/* Owner ruling 2026-08-25 (BOXY-214/215).
                    ⚠️ NOT redundant with the Select Service step's copy, and the
                    reason is a case neither of us saw until it was tested: when
                    a merchant's PROVINCE RULE cannot be used but their ACCOUNT
                    DEFAULT can, `source` is a decided one, the step is SKIPPED,
                    and a rule of theirs silently did nothing. That merchant
                    NEVER sees the step — so this is the only place they are
                    told. See ServicePreferenceResolverTest::
                    test_a_failed_province_rule_still_reports_while_the_account_default_resolves */}
                <UnavailablePreferenceNotice
                  unavailable={
                    usesServices ? (serviceResolution?.unavailable_preference ?? null) : null
                  }
                  className='my-2'
                />
                <SummaryRow
                  label={String(t('delivery_date', 'Delivery Date'))}
                  value={(() => {
                    const d = new Date();
                    if (selectedCarrier.delivery_day !== 'same') d.setDate(d.getDate() + 1);
                    return d.toLocaleDateString(isRTL ? 'ar-IQ' : 'en-GB', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                    });
                  })()}
                />
                <SummaryRow
                  label={String(t('type', 'Type'))}
                  value={
                    selectedCarrier.delivery_day === 'same'
                      ? String(t('same_day_delivery', 'Same-day delivery'))
                      : String(t('guaranteed_delivery', 'Guaranteed delivery'))
                  }
                />
              </div>
            </section>

            {/* Order Info */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <SectionHeader icon={FileText} title={String(t('order_info', 'Order Info'))} />
              <div className='flex flex-col divide-y divide-sd-border'>
                <div className='flex items-start justify-between py-2'>
                  <span className='text-sm text-sd-muted-foreground'>
                    {String(t('package_size', 'Package Size'))}
                  </span>
                  <div className='flex items-center gap-1.5'>
                    <span className='text-sm font-medium'>
                      {orderPayload.boxes && orderPayload.boxes.length >= 2
                        ? `${String(t('n_boxes', { count: orderPayload.boxes.length }))} · ${orderPayload.boxes
                            .map((box) => getSizeLabel(box.size))
                            .join(', ')}`
                        : getSizeLabel(orderPayload.size)}
                    </span>
                    {orderPayload.is_fragile && (
                      <img src={fragileIcon} alt='Fragile' className='h-4 w-4' />
                    )}
                    {/* TEMP-12 — cold-chain icon, exact parity with the
                        fragile <img> beside it: bare 16px glyph, no label,
                        name carried by title/aria-label. */}
                    {orderPayload.temperature_control && (
                      <span
                        data-testid='temperature-control-badge'
                        title={String(t('temperature_controlled'))}
                        aria-label={String(t('temperature_controlled'))}
                        className='inline-flex shrink-0'
                      >
                        <Snowflake className='h-4 w-4 text-blue-500' strokeWidth={2} />
                      </span>
                    )}
                  </div>
                </div>
                <SummaryRow
                  label={String(t('order_type', 'Order Type'))}
                  value={getOrderTypeLabel(orderPayload.type)}
                />
                <SummaryRow
                  label={String(t('payment', 'Payment'))}
                  value={getPaymentLabel(orderPayload.payment_type)}
                />
                <SummaryRow
                  label={String(t('pickup', 'Pickup'))}
                  value={getPickupLabel(orderPayload.pick_up_type)}
                />
                {orderPayload.custom_id && (
                  <SummaryRow
                    label={String(t('custom_id', 'Custom ID'))}
                    value={orderPayload.custom_id}
                  />
                )}
              </div>
            </section>
          </div>

          {/* Right — Sticky Cost Sidebar */}
          <div className='lg:sticky lg:top-6 self-start flex flex-col gap-6'>
            {/* Cost Breakdown */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <SectionHeader icon={CreditCard} title={String(t('cost_summary', 'Cost Summary'))} />
              <CostBreakdown
                currency={String(t('iqd'))}
                products={products.map((product: any) => ({
                  label: `${product.title ?? product.name} × ${product.quantity}`,
                  amount: (parseFloat(product.price) || 0) * (product.quantity || 1),
                }))}
                shipping={{
                  name: String(t('boxy_select_service_name')),
                  customerAmount: amounts.customerShipping,
                  merchantAmount: amounts.merchantShipping,
                }}
                boxes={previewBoxes}
                largeOrderFee={
                  previewLargeFee
                    ? { amount: previewLargeFee.fee, reason: previewLargeFee.reason }
                    : undefined
                }
                customerPays={customerPays}
                youReceive={amounts.youReceive}
              />
            </section>

            {/* Payment Info */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-sd-muted-foreground'>
                  {String(t('payment_method', 'Payment Method'))}
                </span>
                <span className='text-sm font-semibold text-sd-foreground'>
                  {getPaymentLabel(orderPayload.payment_type)}
                </span>
              </div>
              <div className='border-t border-x-0 border-b-0 border-dashed border-sd-border mt-3 pt-3 flex items-center justify-between'>
                <span className='text-sm font-semibold text-sd-foreground'>
                  {String(t('customer_will_pay', 'Customer Will Pay'))}
                </span>
                <span className='text-sm font-bold text-sd-foreground tabular-nums' dir='ltr'>
                  {`${Math.round(customerPays).toLocaleString()} ${String(t('iqd'))}`}
                </span>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='absolute bottom-4 start-0 end-0 z-30 px-4 sm:px-8'>
        {/* FS-2a — this order cannot be created; say so here rather than let
            the merchant press SHIP NOW and read the same sentence as a toast. */}
        {previewRefusal ? (
          // Opaque card under the tint: the footer floats over the scrolling
          // page, and a see-through alert read as text printed over the rows.
          <div className='mb-3 rounded-2xl bg-sd-card shadow-lg'>
            <div
              role='alert'
              className='rounded-2xl border border-sd-destructive/40 bg-sd-destructive/10 px-5 py-3 text-sm text-sd-destructive'
            >
              {previewRefusal}
            </div>
          </div>
        ) : null}
        <div
          className={cn(
            'rounded-2xl border border-sd-border bg-sd-card px-5 py-4 flex items-center justify-between gap-3 transition-shadow',
            isScrolledUp && 'shadow-lg',
          )}
        >
          <Link
            /* BACK returns to the order form — there is no carrier step. */
            to={isEditMode && editOrderUid ? `/orders/edit/${editOrderUid}` : ROUTE.ADDNEWORDER}
            state={{ fromWizard: true }}
            className='inline-flex items-center justify-center whitespace-nowrap gap-2 h-10 rounded-md border border-sd-border bg-transparent px-4 text-sm font-medium uppercase text-sd-foreground hover:bg-sd-accent hover:text-sd-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2 transition-colors'
          >
            <ArrowLeft className={cn('h-4 w-4', isRTL && 'rotate-180')} strokeWidth={2} />
            {String(t('back', 'BACK'))}
          </Link>
          <div className='flex items-center gap-3'>
            {isEditMode ? (
              <button
                onClick={() => handleSubmit(false)}
                disabled={!!submitting || !!previewRefusal}
                className='inline-flex items-center justify-center whitespace-nowrap gap-2 h-10 rounded-md bg-sd-primary px-4 text-sm font-medium uppercase text-sd-primary-foreground hover:bg-sd-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2 disabled:opacity-50 transition-colors'
              >
                {submitting ? '...' : String(t('update', 'UPDATE'))}
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleSubmit(false)}
                  disabled={!!submitting || !!previewRefusal}
                  className='inline-flex items-center justify-center whitespace-nowrap h-10 rounded-md border border-sd-border bg-transparent px-4 text-sm font-medium uppercase text-sd-foreground hover:bg-sd-accent hover:text-sd-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2 disabled:opacity-50 transition-colors'
                >
                  {submitting === 'draft' ? '...' : String(t('save_as_draft', 'SAVE AS DRAFT'))}
                </button>
                <button
                  onClick={() => handleSubmit(true)}
                  disabled={!!submitting || !!previewRefusal}
                  className='inline-flex items-center justify-center whitespace-nowrap gap-2 h-10 rounded-md bg-sd-primary px-4 text-sm font-medium uppercase text-sd-primary-foreground hover:bg-sd-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2 disabled:opacity-50 transition-colors'
                >
                  <Truck className='h-4 w-4' strokeWidth={1.5} />
                  {submitting === 'ship' ? '...' : String(t('ship_now', 'SHIP NOW'))}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
